import React, { useState } from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { Input } from '@app/components/common/inputs/Input/Input';
import { notificationController } from '@app/controllers/notificationController';
import { useResetFormOnCloseModal } from '@app/components/forms/ControlForm/useResetFormOnCloseModal';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { Upload } from '@app/components/common/Upload/Upload';
import { Button } from '@app/components/common/buttons/Button/Button';
import { UploadOutlined } from '@ant-design/icons';
import { resizeThumbnail } from '@app/utils/utils';
import { InputNumber } from '@app/components/common/inputs/InputNumber/InputNumber';
import { normFile } from '@app/utils/file';
import { Hint } from '../ui/Hint';
import { createInspiration, UpdateInspirationData, updateInspirationImage } from '@app/api/inspiration.api';

interface CreateCouponModalProps {
  open: boolean;
  onCancel: () => void;
  onFinish: () => void;
}
interface NewFormData {
  link: string;
  order: number;
  image: File[];
}

export const CreateInspirationModal: React.FC<CreateCouponModalProps> = ({ open, onCancel, onFinish }) => {
  const [form] = BaseForm.useForm();
  const { t } = useTranslation();

  const { isModelsLoading, isActionLoading } = useAppSelector((state) => state.model);

  const [isLoading] = useState(false);

  useResetFormOnCloseModal({
    form,
    open,
  });

  const onOk = async () => {
    form.submit();
  };

  const onFormCancel = async () => {
    onCancel && onCancel();
  };

  const handleSubmit = (values: NewFormData) => {
    const _imageFile: File | any = values.image[0];
    const _data: UpdateInspirationData = {
      link: values.link,
      order: values.order,
    };

    createInspiration(_data).then(async ({ id }) => {
      try {
        await updateInspirationImage(id, _imageFile);
        notificationController.success({
          message: 'Create new inspiration',
          description: 'Successfully',
        });
        onFinish();
      } catch (e) {
        notificationController.error({
          message: 'Create new inspiration',
          description: 'Something went wrong. Please try again later.',
        });
      }
    });
  };

  const imageDummyRequest = async (options: { file: any; onSuccess?: any; onError?: any; onProgress?: any }) => {
    const { onSuccess, onError, file } = options;
    form.setFieldsValue({ image: [file] });

    try {
      onSuccess('Ok');
    } catch (err) {
      onError({ err });
    }
  };

  return (
    <Modal
      title="Create Inspiration"
      open={open}
      onOk={onOk}
      onCancel={onFormCancel}
      confirmLoading={isModelsLoading || isActionLoading || isLoading}
    >
      <BaseForm form={form} layout="vertical" name="create-inspiration" onFinish={handleSubmit}>
        <BaseForm.Item
          name="image"
          label="Image"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          initialValue={[]}
          rules={[{ required: true, message: t('common.requiredField') }]}
        >
          <Upload accept="image/*" maxCount={1} customRequest={imageDummyRequest} listType="picture">
            <Button type="default" icon={<UploadOutlined />}>
              {t('forms.validationFormLabels.clickToUpload')}
            </Button>
          </Upload>
          <Hint>{t('thumbnailUpload.uploadHint')}</Hint>
        </BaseForm.Item>
        <BaseForm.Item name="link" label="Link" rules={[{ required: true, message: t('common.requiredField') }]}>
          <Input />
        </BaseForm.Item>
        <BaseForm.Item name="order" label="Order" rules={[{ required: true, message: t('common.requiredField') }]}>
          <InputNumber />
        </BaseForm.Item>
      </BaseForm>
    </Modal>
  );
};
